// Execute workflow (F3-2) and the endpoint Test call (F3-3): walks a workflow version from Start to an Answer. Every
// API node is one endpoint of the batch; the node runs it in the batch's sandbox (the agent, LH-F5) with the chosen
// test event injected as the lender's reply. Decision nodes evaluate their condition without code; Wait nodes are
// counted against the total wait, not slept. The result is the list of steps and the standard answer (G10).
import type { RunResult } from '@/lib/agent'
import { endpointOf, outcomesOf } from '@/lib/endpoints'
import { edgesFrom, evalCondition } from '@/lib/flow'
import type { AnswerKind, Batch, FlowEdge, FlowNode, ManifestEndpoint, StandardAnswer, TestEvent, WorkflowVersion } from '@/types'

export interface ExecStep {
  nodeId: string
  node: string
  kind: FlowNode['kind']
  port: string
  /** api: the endpoint route */
  endpoint?: string
  /** api: the call the handler made to the lender (headers masked) */
  call?: { method: string; url: string; headers?: Record<string, string>; body?: unknown }
  /** api: the test event used as the lender's reply */
  reply?: { name: string; status: number }
  value?: Record<string, unknown>
  edge?: string
  ms: number
  waitS?: number
  note?: string
  error?: string | null
}

export interface ExecResult {
  steps: ExecStep[]
  answer: StandardAnswer
  answerNode: string | null
  /** the lender codes seen on the way (for G11 capture) */
  codes: { endpoint: string; code: string; outcome: string; reply: TestEvent['reply'] }[]
  ms: number
  waitedS: number
  /** why it stopped if not at an Answer */
  stoppedBy: 'answer' | 'dead-end' | 'total-wait' | 'max-steps' | 'no-start'
}

/** Runs one endpoint in the sandbox: the agent's hub-run (or the in-process agent under vitest). */
export type Invoke = (endpoint: ManifestEndpoint, input: Record<string, unknown>, prev: Record<string, unknown>, reply: TestEvent['reply'] | null) => Promise<RunResult>

export interface ExecOptions {
  applicant: Record<string, unknown>
  env: { environment: 'uat' | 'production'; ref: string }
  /** endpoint route → test event id; endpoints not listed use their first "happy" test event */
  replies: Record<string, string>
  lenderName: string
  /** Lender info's web link: the fallback when the lender's reply has no link */
  fallbackLink: string | null
  invoke: Invoke
  maxSteps?: number
  logId?: string
}

const HAPPY = ['offer', 'approved', 'ok', 'redirect']

/** The test event used for an endpoint when nothing is chosen: the first one with a happy outcome, else the first. */
export function defaultReply(tests: TestEvent[], endpoint: string): TestEvent | undefined {
  const mine = tests.filter((t) => t.endpoint === endpoint)
  return mine.find((t) => HAPPY.includes(t.expectedOutcome)) ?? mine[0]
}

export function newLogId() {
  return `gw_${Math.random().toString(16).slice(2, 8)}…`
}

export async function executeFlow(flow: Pick<WorkflowVersion, 'nodes' | 'edges' | 'settings'>, batch: Batch, opts: ExecOptions): Promise<ExecResult> {
  const t0 = performance.now()
  const steps: ExecStep[] = []
  const codes: ExecResult['codes'] = []
  const stepsByName: Record<string, string> = {}
  let prev: Record<string, unknown> = {}
  let waitedS = 0
  const maxSteps = opts.maxSteps ?? 40
  const logId = opts.logId ?? newLogId()

  const finish = (kind: AnswerKind, message: string, node: FlowNode | null, stoppedBy: ExecResult['stoppedBy']): ExecResult => {
    const offers = Array.isArray(prev.offers)
      ? (prev.offers as { amount: number; ratePa: number; tenureMonths: number; emi?: number }[]).map((o) => ({ amount: o.amount, rate_pa: o.ratePa, tenure_months: o.tenureMonths, ...(o.emi ? { emi: o.emi } : {}) }))
      : []
    const answer: StandardAnswer = {
      status: kind,
      lender_ref: typeof prev.lender_ref === 'string' ? prev.lender_ref : null,
      offers: kind === 'offer' ? offers : [],
      links: { continue: kind === 'rejected' || kind === 'error' ? null : (typeof prev.link === 'string' ? prev.link : opts.fallbackLink) },
      message: message.replace(/\{lender\}/g, opts.lenderName),
      lender_reply: { masked: true, log_id: logId },
    }
    return { steps, answer, answerNode: node?.name ?? null, codes, ms: Math.round(performance.now() - t0), waitedS, stoppedBy }
  }

  const startNode = flow.nodes.find((n) => n.kind === 'start')
  if (!startNode) return finish('error', 'This workflow has no Start node.', null, 'no-start')
  let node: FlowNode = startNode

  for (let i = 0; i < maxSteps; i++) {
    if (node.kind === 'answer') {
      steps.push({ nodeId: node.id, node: node.name, kind: 'answer', port: node.answer ?? 'error', ms: 0 })
      return finish(node.answer ?? 'error', node.message || String(prev.message ?? ''), node, 'answer')
    }
    let port: string
    const step: ExecStep = { nodeId: node.id, node: node.name, kind: node.kind, port: '', ms: 0 }
    if (node.kind === 'start') {
      port = 'next'
      step.note = `standard input: ${Object.keys(opts.applicant).length} fields (PI info, masked in the log)`
    } else if (node.kind === 'wait') {
      port = 'done'
      step.waitS = node.seconds ?? 5
      waitedS += step.waitS
      step.note = `wait ${step.waitS} s (counted, not slept)`
    } else if (node.kind === 'decision') {
      const ctx = { applicant: opts.applicant, prev, steps: stepsByName }
      port = node.condition && evalCondition(node.condition, ctx) ? 'yes' : 'no'
    } else {
      const route = node.endpoint ?? ''
      step.endpoint = route
      const ep = endpointOf(batch.manifest, route)
      const wait = node.waitS ?? flow.settings.waitPerCallS
      if (!ep) {
        port = 'error'
        step.error = `endpoint ${route || '(none)'} is not in batch ${batch.version}`
      } else {
        const chosenId = opts.replies[route]
        const ev = batch.tests.find((t) => t.id === chosenId && t.endpoint === route) ?? defaultReply(batch.tests, route)
        if (!ev) step.note = `no test event for ${route}: the lender gives no reply`
        const r = await opts.invoke(ep, opts.applicant, prev, ev?.reply ?? null)
        const c = r.calls[0]
        if (c) step.call = { method: c.call.method, url: c.call.url, headers: c.call.headers ?? {}, body: c.call.body ?? null }
        if (ev) step.reply = { name: ev.name, status: ev.reply.status }
        step.value = (r.value && typeof r.value === 'object' ? r.value : {}) as Record<string, unknown>
        const ports = outcomesOf(batch.manifest, batch.tests, route)
        port = r.ok && r.outcome && ports.includes(r.outcome) ? r.outcome : 'error'
        if (r.error) step.error = r.error
        else if (!r.ok) step.error = `Not approved: ${r.issues.filter((x) => x.level === 'error').map((x) => x.text).join('; ')}`
        else if (r.outcome && !ports.includes(r.outcome)) step.note = `outcome “${r.outcome}” is not a port of ${route}; taken as error`
        if (r.envMissing.length) step.note = `${step.note ? step.note + ' · ' : ''}missing variables: ${r.envMissing.join(', ')} (Env variables tab)`
        if (ev?.reply.status === 0) waitedS += wait
        const code = typeof step.value.reply_code === 'string' ? step.value.reply_code : null
        if (code && ev) codes.push({ endpoint: route, code, outcome: port, reply: ev.reply })
        // the lender's reference stays known for later steps (offers, status poll) even when a reply does not repeat it
        prev = { ...step.value, lender_ref: step.value.lender_ref ?? prev.lender_ref }
        stepsByName[node.name] = port
        step.ms = r.ms
        step.note = `${step.note ? step.note + ' · ' : ''}wait up to ${wait} s${(node.retries ?? 0) > 0 ? ` · retries ${node.retries}` : ''}`
      }
    }
    step.port = port
    const next: FlowEdge | undefined = edgesFrom(flow, node.id, port).find((e) => !e.condition || evalCondition(e.condition, { applicant: opts.applicant, prev, steps: stepsByName }))
    steps.push(step)
    if (!next) {
      return finish('error', `No line from ${node.name} for “${port}”; the run stops with error.`, null, 'dead-end')
    }
    step.edge = next.id
    if (waitedS > flow.settings.totalWaitS) {
      return finish('pending', '{lender} is still deciding. We will tell you as soon as they answer.', null, 'total-wait')
    }
    const to: FlowNode | undefined = flow.nodes.find((n) => n.id === next.to)
    if (!to) return finish('error', 'A line points to a node that is gone.', null, 'dead-end')
    node = to
  }
  return finish('pending', '{lender} is still deciding.', null, 'max-steps')
}

/** The applicant as the request log shows it: mobile and PAN cut, names and dates hidden. Never stored unmasked. */
export function maskApplicant(a: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(a)) {
    if (k === 'mobile' || k === 'pan') out[k] = `${String(v).slice(0, 5)}…`
    else if (k === 'name' || k === 'dob' || k === 'email') out[k] = '•••'
    else out[k] = v
  }
  return out
}
